import { jwtDecode } from 'jwt-decode';
import axiosInstance from '@/lib/axios';
import {
  saveAuthTokens,
  clearAuthData,
  getAuthToken,
  getRefreshToken
} from '@/lib/auth';

// Axios instance'ı lib/axios.ts'den kullan
export const api = axiosInstance;

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData extends LoginCredentials {
  name: string;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
  permissions?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  data: {
    token: string;
    refreshToken?: string;
    expiresIn?: string;
    user: User;
  }
}

interface DecodedToken {
  id: string;
  iat: number;
  exp: number;
}

export const authService = {
  // Admin kaydı
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/register', data);
    console.log('AuthService - Register response:', response.data);

    const { token, refreshToken, expiresIn } = response.data.data;
    if (token) {
      saveAuthTokens({ token, refreshToken, expiresIn });
      window.dispatchEvent(new Event('storage'));
    }
    return response.data;
  },

  // Admin girişi
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // Super admin login endpoint'ini kullan
      const response = await api.post<AuthResponse>('/auth/login/super-admin', credentials);

      const { token, refreshToken, expiresIn, user } = response.data.data;
      if (token) {
        // Token'ları kaydet (access token + refresh token)
        saveAuthTokens({ token, refreshToken, expiresIn });
        localStorage.setItem('user', JSON.stringify(user));
        window.dispatchEvent(new Event('storage'));
      }

      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // Token ile giriş
  async loginWithToken(token: string): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/auth/verify-login-token', { token });

      const { token: sessionToken, refreshToken, expiresIn, user } = response.data.data;
      if (sessionToken) {
        // Token'ları kaydet (access token + refresh token)
        saveAuthTokens({ token: sessionToken, refreshToken, expiresIn });
        localStorage.setItem('user', JSON.stringify(user));
        window.dispatchEvent(new Event('storage'));
      }

      return response.data;
    } catch (error) {
      console.error('Token login error:', error);
      throw error;
    }
  },

  // Admin çıkışı
  async logout(): Promise<void> {
    console.log('AuthService - Logging out');
    try {
      const refreshToken = getRefreshToken();
      await api.post('/auth/logout', { refreshToken });
    } catch (error) {
      console.error('AuthService - Logout error:', error);
    } finally {
      // Tüm auth verilerini temizle
      clearAuthData();
      window.dispatchEvent(new Event('storage'));
    }
  },

  // Token işlemleri
  setToken(token: string): void {
    console.log('AuthService - Setting token');
    saveAuthTokens({ token });
  },

  getToken(): string | null {
    const token = getAuthToken();
    console.log('AuthService - Getting token:', token ? 'exists' : 'null');
    return token;
  },

  removeToken(): void {
    console.log('AuthService - Removing token');
    clearAuthData();
  },

  // Token kontrolü
  isAuthenticated(): boolean {
    console.log('AuthService - Checking authentication');
    const token = this.getToken();
    if (!token) {
      console.log('AuthService - No token found');
      return false;
    }

    try {
      const decoded = jwtDecode<DecodedToken>(token);
      const currentTime = Date.now() / 1000;
      const isValid = decoded.exp > currentTime;
      console.log('AuthService - Token validation:', { 
        exp: decoded.exp, 
        current: currentTime, 
        isValid 
      });
      return isValid;
    } catch (error) {
      console.error('AuthService - Token decode error:', error);
      this.removeToken();
      return false;
    }
  },

  // Kullanıcı bilgilerini al
  getCurrentUser(): any {
    console.log('AuthService - Getting current user');
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      console.log('AuthService - No user found');
      return null;
    }

    try {
      const user = JSON.parse(userStr);
      console.log('AuthService - User found:', user);
      return user;
    } catch (error) {
      console.error('AuthService - User parse error:', error);
      return null;
    }
  }
};
